/**
 * FeedbackActions (EXACT copy from IntelligenceStudioPage.jsx lines 836-917)
 */

import React, { useState, useEffect, useRef } from 'react';

import { ThumbsUp, ThumbsDown, Check, Copy, RotateCcw } from 'lucide-react';

// ==========================================
// 👍👎 ENTERPRISE FEATURE: FEEDBACK ACTIONS
// ==========================================

const FeedbackActions = ({ messageId, onFeedback, currentFeedback }) => {
  const [showCopied, setShowCopied] = useState(false);
  const [localFeedback, setLocalFeedback] = useState(currentFeedback);
  const copyTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      try {
        if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      } catch (_) {}
    };
  }, []);

  const handleCopy = async content => {
    try {
      await navigator.clipboard.writeText(content);
      setShowCopied(true);
      try {
        if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      } catch (_) {}
      copyTimeoutRef.current = setTimeout(() => setShowCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const handleFeedback = type => {
    const newFeedback = localFeedback === type ? null : type;
    setLocalFeedback(newFeedback);
    onFeedback?.(messageId, newFeedback);
  };

  return (
    <div className="flex items-center gap-1 mt-2">
      {/* Thumbs Up */}
      <button
        onClick={() => handleFeedback('positive')}
        className={`p-1.5 rounded-md transition-all duration-200 ${
          localFeedback === 'positive'
            ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400'
            : 'text-slate-400 dark:text-neutral-500 hover:bg-slate-100 dark:hover:bg-neutral-800 hover:text-slate-600 dark:hover:text-neutral-300'
        }`}
        title="This was helpful"
      >
        <ThumbsUp className="h-3.5 w-3.5" />
      </button>

      {/* Thumbs Down */}
      <button
        onClick={() => handleFeedback('negative')}
        className={`p-1.5 rounded-md transition-all duration-200 ${
          localFeedback === 'negative'
            ? 'bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400'
            : 'text-slate-400 dark:text-neutral-500 hover:bg-slate-100 dark:hover:bg-neutral-800 hover:text-slate-600 dark:hover:text-neutral-300'
        }`}
        title="This wasn't helpful"
      >
        <ThumbsDown className="h-3.5 w-3.5" />
      </button>

      {/* Divider */}
      <div className="w-px h-4 bg-slate-200 dark:bg-neutral-700 mx-1" />

      {/* Copy */}
      <button
        onClick={() =>
          handleCopy(document.querySelector(`[data-message-id="${messageId}"]`)?.innerText || '')
        }
        className="p-1.5 rounded-md text-slate-400 dark:text-neutral-500 hover:bg-slate-100 dark:hover:bg-neutral-800 hover:text-slate-600 dark:hover:text-neutral-300 transition-all duration-200"
        title={showCopied ? 'Copied!' : 'Copy response'}
      >
        {showCopied ? (
          <Check className="h-3.5 w-3.5 text-emerald-500" />
        ) : (
          <Copy className="h-3.5 w-3.5" />
        )}
      </button>

      {/* Regenerate */}
      <button
        onClick={() => onFeedback?.(messageId, 'regenerate')}
        className="p-1.5 rounded-md text-slate-400 dark:text-neutral-500 hover:bg-slate-100 dark:hover:bg-neutral-800 hover:text-slate-600 dark:hover:text-neutral-300 transition-all duration-200"
        title="Regenerate response"
      >
        <RotateCcw className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

export default FeedbackActions;
